import { useEffect, useState } from "react";
import { durationToSeconds } from "@/utils/format";

interface VideoPlayerProps {
  cover: string;
  title: string;
  duration: string;
}

function formatTime(sec: number) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function VideoPlayer({ cover, title, duration }: VideoPlayerProps) {
  const total = durationToSeconds(duration) || 100;
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => {
      setPlayed((prev) => {
        if (prev + 1 >= total) {
          setPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing, total]);

  const progress = Math.min(100, (played / total) * 100);

  const seek = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    setPlayed(Math.floor(ratio * total));
  };

  return (
    <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
      <img
        src={cover}
        alt={title}
        title={`${title} - 呱呱视频社区`}
        className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${playing ? "opacity-30" : "opacity-100"}`}
      />

      {!playing && (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 cursor-pointer group"
          aria-label="播放视频"
        >
          <span className="relative flex items-center justify-center">
            <span className="absolute w-20 h-20 rounded-full bg-white/30 animate-pulse-ring" />
            <span className="relative w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform group-hover:scale-105">
              <i className="ri-play-fill text-3xl md:text-4xl" aria-hidden="true"></i>
            </span>
          </span>
          <span className="mt-4 text-white text-sm">点击播放 · 本页为前端演示播放</span>
        </button>
      )}

      {playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex items-end gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="w-2 bg-primary-400 rounded-full animate-pulse"
                style={{ height: `${12 + (i % 3) * 14}px`, animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 md:px-4 py-3">
        <button
          type="button"
          onClick={seek}
          className="w-full h-1.5 rounded-full bg-white/30 cursor-pointer overflow-hidden mb-3"
          aria-label="播放进度"
        >
          <span className="block h-full bg-primary-500 transition-all" style={{ width: `${progress}%` }} />
        </button>
        <div className="flex items-center gap-3 text-white">
          <button
            type="button"
            onClick={() => setPlaying((v) => !v)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 cursor-pointer"
            aria-label={playing ? "暂停" : "播放"}
          >
            <i className={playing ? "ri-pause-fill text-xl" : "ri-play-fill text-xl"} aria-hidden="true"></i>
          </button>
          <span className="text-xs tabular-nums">
            {formatTime(played)} / {duration}
          </span>
          <div className="flex-1" />
          <span className="hidden sm:flex items-center gap-1 text-xs text-white/70">
            <i className="ri-hd-line" aria-hidden="true"></i>
            演示画质
          </span>
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 cursor-pointer"
            aria-label="全屏"
          >
            <i className="ri-fullscreen-line text-lg" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  );
}